/**
 * @fileoverview Core tracking manager that orchestrates the different tracking services.
 */

/**
 * TrackingManager class responsible for managing multiple tracking services
 * and providing a unified API for tracking events.
 */
class TrackingManager {
  /**
   * Create a new TrackingManager instance
   * @param {Object} [options] - Configuration options
   * @param {TrackerDebugOptions} [options.debug] - Debug options
   */
  constructor(options = {}) {
    /**
     * @type {Map<TrackingServiceName, TrackingService>}
     * @private
     */
    this.services = new Map();

    /**
     * @type {TrackerDebugOptions}
     * @private
     */
    this.debugOptions = {
      enabled: options.debug?.enabled || false,
      logInitialization: options.debug?.logInitialization !== false,
      logEvents: options.debug?.logEvents !== false,
    };

    if (this.debugOptions.enabled) {
      console.log(
        "[TrackingManager] Initialized with debug mode:",
        this.debugOptions,
      );
    }
  }

  /**
   * Register a tracking service with this manager
   * @param {TrackingServiceName} name - The name identifier for the service
   * @param {TrackingService} service - The tracking service implementation
   */
  registerService(name, service) {
    this.services.set(name, service);
    if (this.debugOptions.enabled) {
      console.log(`[TrackingManager] Registered service: ${name}`);
    }
  }

  /**
   * Initialize all registered services
   * @param {Object} config - Configuration for all services
   * @returns {Promise<void>}
   */
  async initialize(config = {}) {
    const initializationPromises = [];

    for (const [name, service] of this.services.entries()) {
      if (this.debugOptions.enabled && this.debugOptions.logInitialization) {
        console.log(`[TrackingManager] Initializing service: ${name}`);
      }

      try {
        const serviceConfig = config[name] || {};
        const initPromise = service
          .initialize(serviceConfig, this.debugOptions)
          .then((success) => {
            if (
              this.debugOptions.enabled &&
              this.debugOptions.logInitialization
            ) {
              console.log(
                `[TrackingManager] Service ${name} initialization ${success ? "successful" : "failed"}`,
              );
            }
            return { name, success };
          })
          .catch((error) => {
            console.error(
              `[TrackingManager] Error initializing service ${name}:`,
              error,
            );
            return { name, success: false, error };
          });

        initializationPromises.push(initPromise);
      } catch (error) {
        console.error(
          `[TrackingManager] Error starting initialization for service ${name}:`,
          error,
        );
        initializationPromises.push(
          Promise.resolve({ name, success: false, error }),
        );
      }
    }

    const results = await Promise.all(initializationPromises);
    const allSucceeded = results.every((result) => result.success);

    if (this.debugOptions.enabled) {
      console.log(
        `[TrackingManager] All services initialized. Overall success: ${allSucceeded}`,
      );
    }

    return allSucceeded;
  }

  /**
   * Track an event across all registered services
   * @param {TrackingEvent} event - The event to track
   * @returns {Promise<void>}
   */
  async trackEvent(event) {
    if (!event || !event.name) {
      console.error("[TrackingManager] Invalid event:", event);
      return;
    }

    if (this.debugOptions.enabled && this.debugOptions.logEvents) {
      console.log(
        `[TrackingManager] Tracking event: ${event.name}`,
        event.params || {},
      );
    }

    const trackingPromises = [];

    for (const [name, service] of this.services.entries()) {
      try {
        const trackPromise = service.trackEvent(event).catch((error) => {
          console.error(
            `[TrackingManager] Error tracking event in service ${name}:`,
            error,
          );
        });

        trackingPromises.push(trackPromise);
      } catch (error) {
        console.error(
          `[TrackingManager] Error calling trackEvent for service ${name}:`,
          error,
        );
      }
    }

    await Promise.all(trackingPromises);
  }

  /**
   * Set user properties across all registered services
   * @param {UserProperties} properties - The user properties to set
   * @returns {Promise<void>}
   */
  async setUserProperties(properties) {
    if (!properties) {
      console.error("[TrackingManager] Invalid user properties:", properties);
      return;
    }

    if (this.debugOptions.enabled) {
      console.log("[TrackingManager] Setting user properties:", {
        ...properties,
        user_email: properties.user_email ? "[REDACTED]" : undefined,
      });
    }

    const propertiesPromises = [];

    for (const [name, service] of this.services.entries()) {
      try {
        const propertiesPromise = service
          .setUserProperties(properties)
          .catch((error) => {
            console.error(
              `[TrackingManager] Error setting user properties in service ${name}:`,
              error,
            );
          });

        propertiesPromises.push(propertiesPromise);
      } catch (error) {
        console.error(
          `[TrackingManager] Error calling setUserProperties for service ${name}:`,
          error,
        );
      }
    }

    await Promise.all(propertiesPromises);
  }

  /**
   * Reset user to anonymous state across all registered services
   * @returns {Promise<void>}
   */
  async resetUser() {
    if (this.debugOptions.enabled) {
      console.log("[TrackingManager] Resetting user to anonymous state");
    }

    const resetPromises = [];

    for (const [name, service] of this.services.entries()) {
      try {
        const resetPromise = service.resetUser().catch((error) => {
          console.error(
            `[TrackingManager] Error resetting user in service ${name}:`,
            error,
          );
        });

        resetPromises.push(resetPromise);
      } catch (error) {
        console.error(
          `[TrackingManager] Error calling resetUser for service ${name}:`,
          error,
        );
      }
    }

    await Promise.all(resetPromises);
  }

  /**
   * Set debug options for the tracking manager and all services
   * @param {TrackerDebugOptions} options - The debug options
   */
  setDebugOptions(options) {
    this.debugOptions = {
      ...this.debugOptions,
      ...options,
    };

    console.log("[TrackingManager] Debug options updated:", this.debugOptions);
  }
}

export default TrackingManager;

